import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { EvaService } from "src/app/core";
import { User } from "src/app/domains/user";
import { Employee } from "src/app/features/employees/domains/employee";
import { EmployeesService } from "src/app/features/employees/services/employees.service";
import { EvaCompanyService } from "src/app/features/eva-company/services/evaCompany.service";
import { FormulaPopupComponent } from "src/app/features/formula/components/formula-popup/formula-popup.component";
import { EvaSubscriptionService } from "src/app/features/subscription/eva-subscription.service";
import { EvaSubscriptionDiscount } from "src/app/features/subscription/models/eva-subscription-discount.model";
import { EvaSubscription } from "src/app/features/subscription/models/eva-subscription.model";
import { AuthenticationService } from "src/app/services/authentication.service";
import { getDateFromObjectId } from "src/app/shared/helpers/date.helpers";
import { environment } from "src/environments/environment";

import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewChild } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import Intercom, { shutdown as intercomShutdown } from "@intercom/messenger-js-sdk";
import { provideIcons } from "@ng-icons/core";
import {
  heroChatBubbleLeftRightSolid,
  heroFolderPlusSolid,
  heroShieldCheckSolid,
  heroStarSolid,
} from "@ng-icons/heroicons/solid";
import * as Sentry from "@sentry/angular";

import { EvaLayoutService } from "../eva-layout.service";
import { IntercomService } from "src/app/services/intercom.service";
import { CommandPaletteService } from "src/app/services/command-palette.service";
import {
  CommandItem,
  CommandSection,
  EvaCommandPaletteComponent,
} from "src/app/shared/ds/eva-command-palette/eva-command-palette.component";
import {
  EvaCommandPaletteRecentsService,
  RecentItemType,
} from "src/app/shared/ds/eva-command-palette/eva-command-palette-recents.service";
import { SearchItem, SearchObjectType } from "src/app/domains/searchItem";
import { Subject, of } from "rxjs";
import { catchError, debounceTime, finalize, map, switchMap } from "rxjs/operators";

@Component({
  selector: "app-eva-layout",
  templateUrl: "./eva-layout.component.html",
  styleUrls: ["./eva-layout.component.scss"],
  viewProviders: [
    provideIcons({
      heroShieldCheckSolid,
      heroChatBubbleLeftRightSolid,
      heroFolderPlusSolid,
      heroStarSolid,
    }),
  ],
})
export class EvaLayoutComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  currentUser: User;

  isMobile: boolean = false;
  isMenuCollapsed: boolean = false;
  isMenuHovered: boolean = false;
  isMobileMenuToggled: boolean = false;

  employee: Employee;

  public evaSubscription: EvaSubscription = null;

  currentDiscount: EvaSubscriptionDiscount;

  public overageBannerClosed: boolean = false;

  upgradeFormulaPopup = true;
  showFormulaPrices = false;

  showGlobalLoader: boolean = false;

  private intercomInitialized = false;

  // Command Palette
  @ViewChild("commandPalette") commandPaletteRef!: EvaCommandPaletteComponent;
  commandPaletteVisible: boolean = false;
  commandPaletteSections: CommandSection[] = [];
  commandPaletteLoading: boolean = false;
  commandPaletteEmptyMessage: string = "Aucun résultat trouvé";
  private searchDebounce$ = new Subject<string>();

  constructor(
    private evaLayoutService: EvaLayoutService,
    private employeeService: EmployeesService,
    public evaCompanyService: EvaCompanyService,
    private evaSubscriptionService: EvaSubscriptionService,
    private evaService: EvaService,
    private authService: AuthenticationService,
    private router: Router,
    private intercomService: IntercomService,
    private commandPaletteService: CommandPaletteService,
    private recentsService: EvaCommandPaletteRecentsService,
    private cdr: ChangeDetectorRef,
  ) {}

  // ==========================================================================
  // Command Palette - Methods
  // ==========================================================================

  private initCommandPalette(): void {
    // Subscribe to service open/close state
    this.commandPaletteService.isOpen$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isOpen) => {
      this.commandPaletteVisible = isOpen;
      if (isOpen) {
        this.loadDefaultSections();
      }
    });

    // Debounced search
    this.searchDebounce$
      .pipe(
        debounceTime(150),
        switchMap((query) => {
          const sanitizedQuery = this.commandPaletteService.sanitizeQuery(query);
          const parsed = this.commandPaletteService.parseQuery(sanitizedQuery);

          if (!sanitizedQuery) {
            this.commandPaletteLoading = false;
            this.commandPaletteEmptyMessage = "Aucun résultat trouvé";
            return of(this.loadDefaultSections());
          }

          if (!parsed.isSlashCommand && sanitizedQuery.length < 2) {
            this.commandPaletteLoading = false;
            this.commandPaletteEmptyMessage = "Tapez au moins 2 caractères pour rechercher";
            return of(this.getStaticCommandSections(sanitizedQuery));
          }

          this.commandPaletteLoading = true;
          this.commandPaletteEmptyMessage = "Aucun résultat trouvé";
          this.cdr.markForCheck();

          if (parsed.isSlashCommand && parsed.slashCommand) {
            const cmd = parsed.slashCommand;

            // Navigation mode
            if (cmd.mode === "navigation") {
              const navSection = this.commandPaletteService.getNavigationSection(parsed.query);
              return of(navSection.items.length > 0 ? [navSection] : []).pipe(
                finalize(() => {
                  this.commandPaletteLoading = false;
                  this.cdr.markForCheck();
                }),
              );
            }

            // Actions mode
            if (cmd.mode === "actions") {
              const actionsSection = this.commandPaletteService.getActionsSection(parsed.query);
              return of(actionsSection.items.length > 0 ? [actionsSection] : []).pipe(
                finalize(() => {
                  this.commandPaletteLoading = false;
                  this.cdr.markForCheck();
                }),
              );
            }

            // Filtered search by type
            if (cmd.filterTypes) {
              if (!parsed.query || parsed.query.trim().length < 2) {
                this.commandPaletteLoading = false;
                this.commandPaletteEmptyMessage = "Tapez au moins 2 caractères pour rechercher";
                return of([]);
              }
              return this.commandPaletteService.search(parsed.query, cmd.filterTypes).pipe(
                catchError(() => {
                  this.commandPaletteEmptyMessage = "Une erreur est survenue pendant la recherche";
                  return of([]);
                }),
                finalize(() => {
                  this.commandPaletteLoading = false;
                  this.cdr.markForCheck();
                }),
              );
            }
          }

          // Standard search
          return this.commandPaletteService.search(sanitizedQuery).pipe(
            map((dynamicSections) => [...this.getStaticCommandSections(sanitizedQuery), ...dynamicSections]),
            catchError(() => {
              this.commandPaletteEmptyMessage = "Une erreur est survenue pendant la recherche";
              return of(this.getStaticCommandSections(sanitizedQuery));
            }),
            finalize(() => {
              this.commandPaletteLoading = false;
              this.cdr.markForCheck();
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((sections) => {
        this.commandPaletteSections = sections;
        this.cdr.markForCheck();
      });
  }

  private loadDefaultSections(): CommandSection[] {
    const sections: CommandSection[] = [];

    // Actions section
    const actionsSection = this.commandPaletteService.getActionsSection();
    if (actionsSection.items.length > 0) {
      sections.push(actionsSection);
    }

    // Navigation section
    const navSection = this.commandPaletteService.getNavigationSection();
    if (navSection.items.length > 0) {
      sections.push(navSection);
    }

    this.commandPaletteSections = sections;
    this.commandPaletteEmptyMessage = "Aucun résultat trouvé";
    this.cdr.markForCheck();
    return sections;
  }

  private getStaticCommandSections(query?: string): CommandSection[] {
    const sections: CommandSection[] = [];
    const actionsSection = this.commandPaletteService.getActionsSection(query);
    const navSection = this.commandPaletteService.getNavigationSection(query);

    if (actionsSection.items.length > 0) {
      sections.push(actionsSection);
    }

    if (navSection.items.length > 0) {
      sections.push(navSection);
    }

    return sections;
  }

  private readonly SEARCH_TYPE_TO_RECENT: Record<SearchObjectType, RecentItemType> = {
    [SearchObjectType.PROJECT]: "project",
    [SearchObjectType.OFFER]: "quote",
    [SearchObjectType.CUSTOMER]: "customer",
    [SearchObjectType.EMPLOYEE]: "employee",
    [SearchObjectType.INVOICE_ISSUED]: "invoice",
    [SearchObjectType.INVOICE_RECEIVED]: "invoice",
    [SearchObjectType.SUPPLIER]: "supplier",
    [SearchObjectType.CONTACT]: "contact",
    [SearchObjectType.INTERVENTION]: "intervention",
    [SearchObjectType.PURCHASE_ORDER]: "purchase_order",
    [SearchObjectType.CONTRACT]: "contract",
    [SearchObjectType.WORKSHOP]: "workshop",
    [SearchObjectType.INTERNAL_ARTICLE]: "internal_article",
  };

  onCommandPaletteSearch(query: string): void {
    const sanitizedQuery = this.commandPaletteService.sanitizeQuery(query);
    if (!sanitizedQuery) {
      this.loadDefaultSections();
      return;
    }

    // If it starts with "/", parse as slash command
    if (sanitizedQuery.startsWith("/")) {
      this.searchDebounce$.next(sanitizedQuery);
      return;
    }

    // Standard search with debounce
    this.searchDebounce$.next(sanitizedQuery);
  }

  onCommandPaletteSelect(item: CommandItem): void {
    // Direct navigation for recent document items
    if (item.data?.isRecent && item.data?.recent?.route) {
      this.router.navigate([item.data.recent.route]);
      this.commandPaletteVisible = false;
      this.commandPaletteService.close();
      return;
    }

    // Save to document recents when a search result is selected
    if (item.data?.searchItem) {
      const searchItem = item.data.searchItem as SearchItem;
      if (searchItem.object_link) {
        this.recentsService.addRecent({
          id: `${searchItem.object_category}-${searchItem.object_id}`,
          label: searchItem.object_label,
          type: this.SEARCH_TYPE_TO_RECENT[searchItem.object_category] ?? "project",
          route: searchItem.object_link,
        });
      }
    }

    // Close the palette
    this.commandPaletteVisible = false;
    this.commandPaletteService.close();
  }

  onCommandPaletteShow(): void {
    this.loadDefaultSections();
  }

  onCommandPaletteHide(): void {
    this.commandPaletteService.close();
  }

  ngOnInit(): void {
    this.evaSubscriptionService.currentSubscription$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((evaSubscription: EvaSubscription) => {
        this.evaSubscription = evaSubscription;

        if (!evaSubscription?.formula_overage_blocked) {
          this.overageBannerClosed = false;
        }

        if (this.evaSubscription?.status === "TRIAL") {
          let checkDiscounts = true;

          let discountBarClosedTs = localStorage.getItem("discountBarClosedTs");
          if (discountBarClosedTs) {
            const discountBarClosedDate = new Date(discountBarClosedTs);
            // Check if the discount bar has been closed for more than 7 days
            if (new Date().getTime() - discountBarClosedDate.getTime() > 7 * 24 * 60 * 60 * 1000) {
              checkDiscounts = true;
            } else {
              checkDiscounts = false;
            }
          }

          if (checkDiscounts) {
            this.evaSubscriptionService.getCurrentDiscount().subscribe((discount) => {
              this.currentDiscount = discount;
            });
          }
        }
      });

    this.authService.currentUser.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
      if (user && user.user_id) {
        this.currentUser = user;
      } else if (!user) {
        this.router.navigate(["/login"]);
      }
    });

    this.evaLayoutService.isMobile$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isMobile) => {
      this.isMobile = isMobile;
    });

    this.evaLayoutService.isMenuCollapsed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((collapsed) => {
      this.isMenuCollapsed = collapsed;
    });

    this.evaLayoutService.isMenuHovered$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((hovered) => {
      this.isMenuHovered = hovered;
    });

    this.evaLayoutService.isMobileMenuToggled$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isMobileMenuToggled) => {
        this.isMobileMenuToggled = isMobileMenuToggled;
      });

    this.employeeService
      .getCurrentEmployee()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (employee) => {
          if (!employee) return;
          this.loadIntercom(employee);
          this.loadSentry(employee);
        },
        (err) => {
          console.error("Failed to load employee", err);
        },
      );

    this.evaService.showGlobalLoader$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((show) => {
      this.showGlobalLoader = show;
      this.cdr.detectChanges();
    });

    this.handlePageChange(this.router.url);

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.handlePageChange(event.urlAfterRedirects);
      }
    });

    this.evaCompanyService.companyChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ employee }) => {
      this.employee = employee;
      this.loadSentry(employee);
    });

    // Command Palette
    this.initCommandPalette();
  }

  handlePageChange(page: string) {
    if (this.evaLayoutService.isCollapseLocked) return;
    if (
      page.startsWith("/offers/") ||
      page.startsWith("/superadmin/") ||
      page.startsWith("/scan-emails") ||
      page.startsWith("/settings") ||
      page.startsWith("/employees/settings")
    ) {
      if (!this.isMenuCollapsed) this.evaLayoutService.toggleMenuCollapsed();
    } else {
      if (this.isMenuCollapsed) this.evaLayoutService.toggleMenuCollapsed();
    }
  }

  loadIntercom(employee: Employee) {
    const company = this.evaCompanyService.currentEvaCompany || this.evaCompanyService.getCurrentEvaCompany() || null;

    if (!company) {
      setTimeout(() => this.loadIntercom(employee), 300);
      return;
    }

    if (this.intercomInitialized) {
      this.intercomService.setReady(true);
      return;
    }

    try {
      intercomShutdown();
    } catch (_) {}
    this.intercomInitialized = true;
    Intercom({
      app_id: environment.intercomId,
      email: employee.email,
      user_id: employee.email,
      created_at: Math.floor(new Date(getDateFromObjectId(employee.employee_id)).getTime() / 1000), // Convert date to timestamp
      name: employee.display_name,
      user_hash: employee.user_hash,
      phone: employee.phone ? employee.phone : employee.phone_personnal,
      employee_type: employee.employee_position_current.employee_type,
      // web_version: environment.appVersion,
      company: {
        company_id: company.eva_company_id, // Include company_id
        id: company.eva_company_id,
        name: company.name,
        website: company.url,
        plan: this.evaSubscription?.formula,
        status: this.evaSubscription?.status,
        start_trial_date: this.evaSubscription?.trial_start_date,
        end_trial_date: this.evaSubscription?.trial_end_date,
        trial_grace_delay_used: this.evaSubscription?.trial_grace_delay_used,
        interval_billing: this.evaSubscription?.interval,
        stripe_customer_id: this.evaSubscription?.stripe_customer_id,
        forced_env: this.evaSubscription?.env,
      },
      // employee_type: employee.employee_type,
      hide_default_launcher: true,
      // widget: {
      //   activator: "#intercom",
      // },
    });
    this.intercomService.setReady(true);
  }

  loadSentry(employee: Employee) {
    Sentry.setUser({
      email: employee.email,
      id: employee.user_id,
      username: employee.display_name,
    });
    Sentry.setTag("eva_company_id", this.evaCompanyService.currentEvaCompany.eva_company_id);
    Sentry.setTag("eva_company_name", this.evaCompanyService.currentEvaCompany.name);
    Sentry.setTag("employee_type", employee.employee_type);
  }

  showIntercom() {
    this.intercomService.openMessenger();
  }

  toggleCollapse() {
    this.evaLayoutService.toggleMenuCollapsed();
  }

  toggleMobileSidebar() {
    this.evaLayoutService.toggleMobileMenuToggled();
  }

  openFormula() {
    this.evaSubscriptionService.showFormulaPopup(null, true);
  }

  closeDiscountBar() {
    localStorage.setItem("discountBarClosedTs", new Date().toISOString());
    this.currentDiscount = null;
  }

  openOverageFormula() {
    this.evaSubscriptionService.showFormulaPopup(null, true);
  }

  closeOverageBanner() {
    this.overageBannerClosed = true;
  }

  closeUpgradeFormula() {
    this.upgradeFormulaPopup = false;
  }
}
